/**
 * Password-reset screen with two phases: request (enter the account email) then
 * confirm (the emailed 6-digit code + a new password). On a successful reset the
 * owner is signed in automatically and taken straight to their dashboard, mirroring
 * the sign-up gate, so a returning owner who forgot their password lands back in the
 * app without a second trip through /signin.
 *
 * The flow never reveals whether an address has an account: the request step always
 * advances to the confirm step with the same neutral copy, and a Cognito
 * `UserNotFoundException` is swallowed rather than surfaced (no account enumeration).
 */
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button, Field, Notification } from '@nuff/ui/react';

import { useAuth } from '../auth/context';
import { CodeInput } from '../auth/CodeInput';
import { cognitoCode, friendlyAuthMessage, PASSWORD_HINT } from '../auth/errors';

type Phase = 'request' | 'confirm';

export function ResetPassword(): React.JSX.Element {
  const { status, forgotPassword, confirmForgotPassword, signIn } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'signedIn') {
    return <Navigate to="/app" replace />;
  }

  const neutralNotice = `If an account exists for ${email}, we sent a reset code. Check your email.`;

  async function handleRequest(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setNotice(neutralNotice);
      setPhase('confirm');
    } catch (caught) {
      // Never reveal whether the address has an account: a missing user advances to
      // the confirm step exactly like a real one (the code simply never arrives).
      if (cognitoCode(caught) === 'UserNotFoundException') {
        setNotice(neutralNotice);
        setPhase('confirm');
      } else {
        setError(friendlyAuthMessage(caught, 'Could not start the password reset.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmForgotPassword(email, code, password);
      // Auto-sign-in with the new credentials and go straight to the dashboard, mirroring
      // the sign-up gate, so the owner never has to re-enter what they just chose.
      await signIn(email, password);
      await navigate('/app');
    } catch (caught) {
      setError(friendlyAuthMessage(caught, 'Could not reset the password.'));
      setCode('');
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await forgotPassword(email);
      setNotice(`Code sent. Check ${email}.`);
    } catch (caught) {
      // A resend for a missing account is a no-op that still reads as "sent".
      if (cognitoCode(caught) === 'UserNotFoundException') {
        setNotice(`Code sent. Check ${email}.`);
      } else {
        setError(friendlyAuthMessage(caught, 'Could not resend the code.'));
      }
    }
  }

  if (phase === 'confirm') {
    return (
      <section className="auth" aria-labelledby="reset-title">
        <h1 id="reset-title" className="auth__title">
          Set your password
        </h1>
        <p className="auth__lead">Enter the 6-digit code we emailed and choose a new password.</p>
        {notice !== null && (
          <p className="auth__status" role="status">
            {notice}
          </p>
        )}
        <form className="auth__form" onSubmit={(event) => void handleConfirm(event)} noValidate>
          {error !== null && <Notification variant="error">{error}</Notification>}
          <label className="auth__label" htmlFor="reset-code">
            Reset code
          </label>
          <CodeInput
            label="Reset code"
            value={code}
            onChange={setCode}
            onComplete={setCode}
            disabled={submitting}
            autoFocus
          />
          <Field
            label="New password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            required
            hint={PASSWORD_HINT}
            value={password}
            onChange={setPassword}
          />
          <Button
            className="app__btn-start"
            type="submit"
            disabled={submitting || code.length < 6 || password.length === 0}
          >
            {submitting ? 'Setting password…' : 'Set new password'}
          </Button>
        </form>
        <p className="auth__alt">
          Didn&rsquo;t get it?{' '}
          <button
            type="button"
            className="auth__linkbtn"
            onClick={() => void handleResend()}
            disabled={submitting}
          >
            Resend code
          </button>
        </p>
      </section>
    );
  }

  return (
    <section className="auth" aria-labelledby="reset-title">
      <h1 id="reset-title" className="auth__title">
        Reset your password
      </h1>
      <p className="auth__lead">
        Enter your email and we will send a code to set a new password.
      </p>
      <form className="auth__form" onSubmit={(event) => void handleRequest(event)} noValidate>
        {error !== null && <Notification variant="error">{error}</Notification>}
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={setEmail}
        />
        <Button className="app__btn-start" type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset code'}
        </Button>
      </form>
      <p className="auth__alt">
        Remembered it? <Link to="/signin">Sign in</Link>
      </p>
    </section>
  );
}
